// [목적] 폴리오 시작 템플릿(빈 폴리오/여행 코스/축제 후기) 정의 — CreateFolioInput 프리셋 단일 소스
// [이유] /me/folios 빈 상태의 "템플릿으로 시작" 진입점이 AI 생성(/chat) 없이도 폴리오를 만들 수 있게 한다.
//         기존 생성 경로는 RAG 파이프라인(source='ai') 뿐이라 "직접 쓰고 싶은 사용자"의 경로가 없었다.
//         POST /folios(createFolioSchema) 는 이미 days/items 동시 생성을 지원하므로 BE 신규 API 없이 구현된다.
// [사이드 이팩트] 없음(순수 데이터). build() 는 호출 시점마다 새 객체를 만들어 호출부의 변형이 서로 섞이지 않는다.
// [수정 시 고려사항] days 최대 10개 / items 최대 30개 / title 200자 — packages/types createFolioSchema 제약을 넘기면
//                   BE 400. 항목 title 은 사용자가 편집 화면에서 지울 전제의 "빈칸 안내문" 성격으로 짧게 유지할 것.
//                   source 는 'manual' 고정 — 'ai' 는 RAG 생성분만 사용(통계/배지 집계가 source 를 구분한다).

import type { CreateFolioInput } from '@gangwoncanvas/types';

export type FolioTemplateId = 'blank' | 'course' | 'festival';

export interface FolioTemplate {
  /** 템플릿 식별자 — 분석 이벤트/테스트에서 참조 */
  id: FolioTemplateId;
  /** 카드 제목 */
  label: string;
  /** 카드 아이콘(장식용 — aria-hidden 으로 렌더할 것) */
  icon: string;
  /** 카드 보조 설명 */
  description: string;
  /** POST /folios 로 보낼 입력 생성 */
  build: () => CreateFolioInput;
}

export const FOLIO_TEMPLATES: ReadonlyArray<FolioTemplate> = [
  {
    id: 'blank',
    label: '빈 폴리오',
    icon: '📄',
    description: '제목만 잡고 처음부터 자유롭게 구성해요.',
    build: () => ({
      title: '제목 없는 폴리오',
      visibility: 'private',
      source: 'manual',
    }),
  },
  {
    id: 'course',
    label: '여행 코스',
    icon: '🗺️',
    description: '1박 2일 일정 뼈대가 미리 깔린 템플릿이에요.',
    build: () => ({
      title: '새 여행 코스',
      subtitle: '1박 2일 강원 여행',
      vibe: ['여행코스'],
      durationNights: 1,
      durationDays: 2,
      visibility: 'private',
      source: 'manual',
      days: [
        {
          dayIndex: 1,
          summary: '첫날 동선을 적어보세요.',
          items: [
            { kind: 'place', title: '도착 후 첫 방문지' },
            { kind: 'meal', title: '점심' },
            { kind: 'place', title: '오후 코스' },
            { kind: 'stay', title: '숙소 체크인' },
          ],
        },
        {
          dayIndex: 2,
          summary: '둘째 날 동선을 적어보세요.',
          items: [
            { kind: 'meal', title: '아침' },
            { kind: 'place', title: '오전 코스' },
            { kind: 'transit', title: '귀가 이동' },
          ],
        },
      ],
    }),
  },
  {
    id: 'festival',
    label: '축제 후기',
    icon: '🎪',
    description: '다녀온 축제를 사진·한줄평 중심으로 기록해요.',
    build: () => ({
      title: '축제 후기',
      subtitle: '다녀온 축제 기록',
      vibe: ['축제'],
      durationNights: 0,
      durationDays: 1,
      visibility: 'private',
      source: 'recap',
      days: [
        {
          dayIndex: 1,
          summary: '언제, 누구와 다녀왔는지 적어보세요.',
          items: [
            { kind: 'activity', title: '축제 입장 · 첫인상' },
            { kind: 'place', title: '꼭 본 프로그램' },
            { kind: 'meal', title: '축제 먹거리' },
            { kind: 'note', title: '한줄평 · 다음에 갈 사람에게' },
          ],
        },
      ],
    }),
  },
];

/**
 * [목적] id 로 템플릿 조회 — 라우트 쿼리(`?template=course`) 등 외부 입력 검증용
 * [이유] 호출부가 FOLIO_TEMPLATES.find 를 매번 재작성하지 않도록 한다.
 * [사이드 이팩트] 없음
 * [수정 시 고려사항] 미지의 id 는 undefined 반환 — 호출부에서 폴백 처리할 것
 */
export function findFolioTemplate(id: string): FolioTemplate | undefined {
  return FOLIO_TEMPLATES.find((t) => t.id === id);
}
